/* screens/checkout/PayByLink.jsx — оплата по именной ссылке менеджера (/pay/:token).
   Это КЛИЕНТСКИЙ экран, а не консоль продаж: сюда приходят по ссылке из письма или
   мессенджера, часто с телефона и без активной сессии. Роут прошит ВНЕ RequireAuth —
   гард увёл бы на /signin и потерял токен из пути, поэтому вход инициируем сами,
   сохранив возврат в sessionStorage (PENDING_PAY_LINK_KEY читают SignIn и SignUp).

   СЧЁТ НА СУЩЕСТВУЮЩЕЕ ПРОСТРАНСТВО ОПЛАЧИВАЕТ КТО УГОДНО (`payable_by_anyone`
   от сервера). Ссылку пересылают бухгалтеру, учредителю, партнёру — у них
   аккаунта нет и не будет, и требование входа не защищало ничего (получателя
   подписки задаёт ссылка, а не сессия), зато останавливало платёж. Поэтому
   ветки «аноним» и «вошёл не тот аккаунт» больше не терминальные: они
   объясняют, чей это счёт, и дают оплатить. Вход остаётся ссылкой для того,
   кто пришёл сюда СВОИМ счётом и хочет видеть свою подписку.

   Ветки: загрузка / 404 / ошибка загрузки (в т.ч. 429) / used|revoked|expired /
   оплата (свой счёт либо чужой, оплачиваемый гостем) / «войдите под клиентом»
   для режима «новое пространство». Развилку входа повторяем за InviteAccept
   (S12): сохранить токен → /signin, без принудительного logout.
   Данные — salesConsole (контракт {ok,status,data}, без throw на HTTP-кодах);
   суммы приходят строками DRF. */

import React from 'react';
import { useAuth } from '../../app/providers/AuthProvider.jsx';
import { InvoicePayModal } from '../../components/patterns/InvoicePayModal.jsx';
import { PaymentMethodModal } from '../../components/patterns/PaymentMethodModal.jsx';
import { RecurringConsentModal } from '../../components/patterns/RecurringConsentModal.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Field, TextInput } from '../../components/ui/Field.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { getPaymentLink, startPaymentLinkCheckout, startPaymentLinkInvoice } from '../../lib/api/salesConsole.js';
import { fmt } from '../../lib/format.js';
import { consentNeededForLink, isConsentRequiredError } from '../../lib/recurringConsent.js';
import { RR } from '../../lib/router.jsx';
import { usePhoneGate } from '../../components/patterns/PhoneGate.jsx';
import { CheckoutLayout } from './CheckoutLayout.jsx';
import { CheckoutRow } from './CheckoutRow.jsx';

  const { useState, useEffect, useCallback, useRef } = React;
  const { useNavigate, useParams } = RR;

  // Свой ключ возврата, отдельный от pending_invite_token: сценарии независимы, и
  // оплата не должна перехватывать возврат приглашения (и наоборот).
  const PENDING_PAY_LINK_KEY = 'pending_payment_link_token';

  const days = (n) => (n ? `${n} ${fmt.plural(n, 'день', 'дня', 'дней')}` : '—');
  // Контракт лимитов: -1 = безлимит, 0 = функция запрещена (models.py, quota.UNLIMITED).
  // Рисовать их буквально нельзя: клиент прочитал бы «-1 запросов», а безлимитный
  // голос (-1 → Math.round(-1/60) → -0) превратился бы в «0 минут», то есть в
  // «голоса нет». Консоль менеджера прямо предлагает завести такой тариф.
  const UNLIMITED = -1;
  const requests = (n) => (n === UNLIMITED
    ? 'Без ограничений'
    : `${n} ${fmt.plural(n, 'запрос', 'запроса', 'запросов')} в месяц`);
  // Квота голоса в контракте — секунды; клиенту секунды ни о чём не говорят.
  const voiceMinutes = (sec) => {
    if (sec === UNLIMITED) return 'Без ограничений';
    const m = Math.round(sec / 60);
    return `${m} ${fmt.plural(m, 'минута', 'минуты', 'минут')} в месяц`;
  };
  // Квоту 0 («функция запрещена») в счёте не показываем вовсе: строка
  // «Голосовой ввод — 0 минут» читается как поломка, а не как состав тарифа.
  const hasQuota = (v) => v != null && v !== 0;
  // amount_rub/price_rub — строки ("7450.00"), fmt.money ждёт число.
  const money = (v) => (v == null || v === '' ? '—' : fmt.money(Number(v)));

  /** Кто перед нами на странице счёта → флаги, решающие, что показать.
   *
   * Вынесено в чистую функцию и заморожено vitest'ом не ради стиля: от этих
   * пяти флагов зависит, увидит ли человек кнопку оплаты и спросим ли мы
   * телефон, — то есть деньги. Ровно на таком отборе раздел ссылок уже уезжал
   * в прод нерабочим (PR #115): условие на фронте дублировало серверное поле,
   * которого в ответе не было, и `undefined && …` тихо гасил весь экран.
   * Поэтому: `payable_by_anyone` берётся у СЕРВЕРА как есть и НЕ выводится
   * здесь из `mode` — одно денежное правило живёт в одном месте.
   *
   * Аргумент — объект ответа `/api/subscription/pay/<token>/` как есть (или
   * null, пока он не загружен).
   */
  function payerView(link) {
    const mine = !!(link && link.is_for_me);
    const anyonePays = !!(link && link.payable_by_anyone);
    return {
      // Счёт выставлен этому аккаунту.
      mine,
      // Платит посторонний: счёт открыт, но подписка не его.
      guestPays: anyonePays && !mine,
      // Показывать состав счёта и кнопку оплаты.
      canPay: mine || anyonePays,
      // Единственная терминальная развилка «войдите под клиентом» — режим
      // «новое пространство»: название заведения знает только клиент.
      needsClientLogin: !!link && !mine && !anyonePays,
    };
  }

  /** Что оплата сделает с текущим сроком → подсказка над кнопкой оплаты или null.
   *
   * Исход называет СЕРВЕР (`period_effect`, billing.link_period_effect — те же
   * предикаты, что применяют оплату), здесь только текст. Прежде страница сама
   * выводила «период начнётся заново, оставшиеся дни не переносятся» из «Сейчас
   * у вас» — для любой ссылки при идущем платном тарифе. После S44 (ссылка на
   * идущий тариф продлевает срок от конца) и очереди акции за любым платным
   * тарифом это стало неправдой тем, у кого дни как раз не сгорают:
   *   extends  — тот же идущий тариф, срок продлится от конца (info);
   *   queued   — акция начнётся после окончания текущего (info);
   *   restarts — другой тариф: срок с сегодня, остаток сгорит (warn, правда).
   * null, неизвестный исход, исход без даты и ОТСУТСТВИЕ ключа (старый бэкенд в
   * окне выката) → null: лучше промолчать, чем снова пообещать сгоревшие дни.
   * Даты сервер шлёт только клиенту ссылки — постороннему плательщику null.
   * Окну способа оплаты (PaymentMethodModal) исход отдаёт дату: `startsAt` у очереди,
   * `extendsTo` у продления — иначе оно пообещало бы «подписка активируется сразу».
   */
  function periodNotice(link) {
    const e = link && link.period_effect;
    if (!e || typeof e !== 'object') return null;
    if (e.kind === 'extends' && e.ends_at && e.new_ends_at) {
      return {
        tone: 'info', icon: 'calendar',
        text: `Текущий тариф оплачен до ${fmt.date(e.ends_at)} — срок продлится до ${fmt.date(e.new_ends_at)}.`,
        // Окну способа оплаты: «продлит текущий срок до …» вместо «активируется сразу».
        extendsTo: e.new_ends_at,
      };
    }
    if (e.kind === 'queued' && e.starts_at) {
      return {
        tone: 'info', icon: 'clock',
        text: `Начнётся ${fmt.date(e.starts_at)}, после окончания текущего тарифа.`,
        // Окну способа оплаты: иначе оно пообещало бы «подписка активируется сразу».
        startsAt: e.starts_at,
      };
    }
    if (e.kind === 'restarts' && e.current_ends_at) {
      return {
        tone: 'warn', icon: 'alert-triangle',
        text: `Текущий тариф оплачен до ${fmt.date(e.current_ends_at)}. После оплаты`
          + ' период начнётся заново с сегодняшнего дня — оставшиеся дни не'
          + ' переносятся. Если это не то, чего вы ждёте, напишите менеджеру'
          + ' до оплаты.',
      };
    }
    return null;
  }

  function PayByLink() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [link, setLink] = useState(null);
    const [phase, setPhase] = useState('loading');   // loading | ready | notFound | error
    // Название будущего заведения (только для ссылок mode='new_workspace').
    // Заготовку мог вписать менеджер — тогда клиент её подтверждает или правит.
    const [wsName, setWsName] = useState('');
    const [wsNameTouched, setWsNameTouched] = useState(false);
    const [errStatus, setErrStatus] = useState(0);
    const [paying, setPaying] = useState(false);
    const phoneGate = usePhoneGate();
    const [payErr, setPayErr] = useState(null);
    const [methodOpen, setMethodOpen] = useState(false);   // развилка способа оплаты
    const [consentOpen, setConsentOpen] = useState(false); // согласие на автосписания
    const [invoiceOpen, setInvoiceOpen] = useState(false); // форма реквизитов (Точка)

    // Флаг живости ставим В эффекте, а не в инициализаторе ref: StrictMode гасит его
    // на размонтировании первого прохода, и без переустановки экран навсегда завис бы
    // на «Загружаем счёт…».
    const alive = useRef(true);
    useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

    // silent — тихий перезапрос после отказа checkout: на экране уже что-то есть,
    // подменять его спиннером значит моргать на ровном месте.
    const load = useCallback(async ({ silent } = {}) => {
      if (!silent) { setPhase('loading'); setPayErr(null); }
      // apiFetch НЕ ловит отказ самого fetch (client.js — голый `await fetch`):
      // контракт {ok,status,data} держится только для HTTP-ответов, а при обрыве
      // связи промис реджектится. Без catch экран навсегда вис бы на «Загружаем
      // счёт…», причём ровно там, где связь и рвётся — на телефоне.
      const r = await getPaymentLink(token).catch(() => ({ ok: false, status: 0, data: null }));
      if (!alive.current) return;
      if (r.ok && r.data) {
        setLink(r.data);
        // Заготовку менеджера подставляем ОДИН раз и только пока клиент поля не
        // трогал: тихий перезапрос после отказа оплаты (silent) не имеет права
        // затереть название, которое человек только что набрал.
        setWsName((cur) => (wsNameTouched || cur ? cur : (r.data.new_workspace_name || '')));
        setPhase('ready');
        return;
      }
      if (silent) return;   // сетевая икота не должна стирать уже показанный счёт
      setErrStatus(r.status);
      setPhase(r.status === 404 ? 'notFound' : 'error');
    }, [token, wsNameTouched]);

    useEffect(() => { load(); }, [load]);

    // Ссылка создаёт пространство, а не меняет тариф существующему. Режим
    // решает СЕРВЕР и присылает его всем, включая анонима: без него страница
    // до входа не может объяснить, что покупают.
    const isNewWs = !!(link && link.mode === 'new_workspace');
    // Кто перед нами. Считаем ЗДЕСЬ, а не ниже у разметки: этими флагами
    // пользуются и обработчики (payClick/payByCard), объявленные выше по файлу.
    const { mine, guestPays, canPay, needsClientLogin } = payerView(link);
    const wsNameClean = wsName.trim();
    // Два символа — не «валидация ради валидации»: имя из одной буквы клиент не
    // узнает в списке пространств, а пустое сервер и так отобьёт.
    const wsNameOk = !isNewWs || wsNameClean.length >= 2;

    // Доступность вторых способов решает СЕРВЕР и присылает вместе со ссылкой
    // (invoice_available / foreign_available — рубильники Точки и инокарт).
    const hasInvoice = !!(link && link.invoice_available);
    const hasForeign = !!(link && link.foreign_available);

    // Развилка способа оплаты — ровно как при покупке тарифа из ЛК: сначала
    // «чем платить», потом уже сам платёж. Сеть от 5 точек, ради которой
    // закрытые тарифы и заведены, — это юрлицо: оно платит по счёту с ИНН и
    // закрывающими актами, а не корпоративной картой. А управляющая компания
    // такой сети вполне может сидеть за границей — отсюда третий способ.
    // Развилку показываем при ЛЮБОМ дополнительном способе: раньше условие
    // спрашивало только про счёт, и включённая инокарта осталась бы невидимой.
    const payClick = () => {
      if (paying) return;
      setPayErr(null);
      if (!wsNameOk) {
        // Показать ошибку под полем, а не гнать запрос: сервер отобьёт его тем
        // же смыслом, но человек увидит отказ на месте, а не после перехода.
        setWsNameTouched(true);
        setPayErr('Укажите название заведения — оно станет вашим новым пространством.');
        return;
      }
      const next = () => {
        if (hasInvoice || hasForeign) { setMethodOpen(true); return; }
        payByCard();
      };
      // Телефон — до выбора способа оплаты: он нужен одинаково и карте, и счёту.
      // Но спрашиваем его ТОЛЬКО у самого клиента: у постороннего плательщика
      // профиля нет, а его номер в карточке клиента подменил бы контакт
      // заведения телефоном бухгалтера. Сервер этот шаг для гостя тоже не
      // делает — модалка была бы формой в никуда.
      if (mine) phoneGate.run(next); else next();
    };

    // method: 'card' | 'foreign_card'. Инокарта — тот же карточный путь и та же
    // Робокасса, разница на бэке: платёж помечается разовым (без Recurring),
    // иначе канал иностранных карт его не пропустит.
    // consent=true — плательщик поставил галочку согласия на автосписания.
    const payByCard = async (method = 'card', consent = false) => {
      if (paying) return;                     // двойной клик = два платежа
      setMethodOpen(false);
      // Ссылка с автопродлением + российская карта привязывает карту плательщика:
      // сперва условия и обязательная галочка, и только потом Робокасса. Сервер
      // без согласия такой платёж не создаст.
      if (!consent && consentNeededForLink(link, method)) { setConsentOpen(true); return; }
      setPaying(true); setPayErr(null);
      // Тот же реджект fetch, что и в load(). Без catch кнопка навсегда
      // осталась бы заблокированной надписью «Переходим к оплате…»: setPaying(false)
      // ниже просто не выполнился бы, и повторить оплату стало бы нечем, кроме
      // перезагрузки страницы.
      const r = await startPaymentLinkCheckout(token, method, isNewWs ? wsNameClean : undefined, consent)
        .catch(() => ({ ok: false, status: 0, data: null }));
      if (r.ok && r.data && r.data.redirect_url) {
        // Уходим на Робокассу. paying НАРОЧНО не снимаем: страница живёт ещё
        // мгновение до перехода, и разблокированная кнопка успела бы создать
        // второй платёж.
        window.location.assign(r.data.redirect_url);
        return;
      }
      if (!alive.current) return;
      // Сервер требует согласие, а экран его не спросил (снимок ссылки устарел):
      // не тупик с красной плашкой, а то же окно с галочкой.
      if (!consent && method !== 'foreign_card' && isConsentRequiredError(r.data)) {
        setPaying(false); setConsentOpen(true);
        return;
      }
      // Отказ после согласия показываем на странице — окно согласия его бы закрыло.
      setConsentOpen(false);
      setPayErr((r.data && r.data.detail)
        || (r.status === 0
          ? 'Не удалось связаться с сервером. Проверьте связь и попробуйте ещё раз.'
          : 'Не удалось начать оплату. Попробуйте ещё раз.'));
      setPaying(false);
      // 403 (не тот аккаунт / не админ пространства) и 409 (ссылка оплачена, отозвана
      // или истекла) значат, что сервер знает о ссылке больше нашего снимка. Без
      // перезапроса экран продолжил бы предлагать оплату несуществующего счёта.
      if (r.status === 403 || r.status === 409) load({ silent: true });
    };

    // Токен живёт в пути, а не в query: после ухода на /signin он бы просто пропал,
    // и клиент оказался бы в чате без счёта. Кладём в sessionStorage — SignIn/SignUp
    // вернут сюда сразу после входа.
    const saveAndSignIn = () => {
      try { sessionStorage.setItem(PENDING_PAY_LINK_KEY, token); } catch { /* приватный режим — переживём */ }
      navigate('/signin');
    };

    // Гостю «домой» = вход: /chat под RequireAuth молча вышвырнул бы его на /signin.
    const homePath = user ? '/chat' : '/signin';
    const homeLabel = user ? 'На главную' : 'Перейти ко входу';

    // ── Загрузка ──
    if (phase === 'loading') {
      return (
        <CheckoutLayout>
          <div className="checkout-state" aria-busy="true">
            <Icon name="spinner" size={24} />
            <span className="text-dim">Загружаем счёт…</span>
          </div>
        </CheckoutLayout>
      );
    }

    // Каркас «одна мысль + одна кнопка»: терминальные ветки отличаются только словами.
    const stateScreen = (icon, tone, title, text, extra) => (
      <CheckoutLayout>
        <div className="paylink">
          <header className="checkout-head checkout-head--center">
            <div className={'checkout-icon checkout-icon--' + tone} aria-hidden="true"><Icon name={icon} size={28} /></div>
            <div className="t-eyebrow text-dim">Оплата подписки</div>
            <h1 className="t-h2">{title}</h1>
          </header>
          <p className="paylink__lead">{text}</p>
          {extra}
          <div className="checkout-actions">
            <Button variant="secondary" size="lg" className="full" onClick={() => navigate(homePath)}>{homeLabel}</Button>
          </div>
        </div>
      </CheckoutLayout>
    );

    // ── Ссылки нет ──
    if (phase === 'notFound') {
      return stateScreen('x-circle', 'bad', 'Ссылка не найдена',
        'Ссылка не найдена или больше не действует. Запросите новую у менеджера.');
    }

    // ── Не удалось загрузить (429 rate_limited, 5xx, обрыв связи) ──
    if (phase === 'error') {
      const rateLimited = errStatus === 429;
      return (
        <CheckoutLayout>
          <div className="paylink">
            <header className="checkout-head checkout-head--center">
              <div className="checkout-icon checkout-icon--warn" aria-hidden="true"><Icon name="alert-triangle" size={28} /></div>
              <div className="t-eyebrow text-dim">Оплата подписки</div>
              <h1 className="t-h2">{rateLimited ? 'Слишком много попыток' : 'Не удалось открыть счёт'}</h1>
            </header>
            <p className="paylink__lead">
              {rateLimited
                ? 'Ссылку открывали слишком часто. Подождите минуту и попробуйте снова — она никуда не денется.'
                : 'Не удалось загрузить счёт. Проверьте связь и попробуйте ещё раз.'}
            </p>
            <div className="checkout-actions">
              <Button variant="primary" size="lg" className="full" iconLeft="refresh" onClick={() => load()}>
                Попробовать снова
              </Button>
              <a className="link checkout-link" href={homePath}
                onClick={(e) => { e.preventDefault(); navigate(homePath); }}>{homeLabel}</a>
            </div>
          </div>
        </CheckoutLayout>
      );
    }

    const d = link || {};
    const plan = d.plan || {};
    const cur = d.current_plan;

    // Короткая выжимка для неактивных ссылок: человеку важно понимать, о каком
    // счёте вообще речь, даже если оплатить его уже нельзя.
    const detailsShort = (plan.name || d.amount_rub != null) ? (
      <div className="checkout-details">
        {plan.name && <CheckoutRow label="Тариф" value={plan.name} />}
        {d.amount_rub != null && (
          <div className="paylink__total"><CheckoutRow label="Сумма" value={money(d.amount_rub)} /></div>
        )}
      </div>
    ) : null;

    // ── Ссылка больше не активна ──
    if (d.status === 'used') {
      const created = d.created_workspace && d.created_workspace.name;
      return stateScreen('check-circle', 'ok', 'Счёт уже оплачен',
        created
          ? `Хорошая новость: по этой ссылке оплата уже прошла, пространство «${created}» создано и тариф в нём подключён. Выбрать его можно в переключателе пространств вверху.`
          : 'Хорошая новость: по этой ссылке оплата уже прошла, тариф подключён — делать ничего не нужно. Если доступ так и не появился, напишите менеджеру.',
        detailsShort);
    }
    if (d.status === 'revoked') {
      return stateScreen('x-circle', 'bad', 'Ссылка отозвана',
        'Менеджер отозвал этот счёт — обычно так делают, когда меняются условия. Оплатить по нему уже нельзя: попросите выставить новый.',
        detailsShort);
    }
    if (d.status === 'expired') {
      const until = d.expires_at ? ` (действовала до ${fmt.date(d.expires_at)})` : '';
      return stateScreen('clock', 'warn', 'Срок ссылки истёк',
        `Срок действия ссылки${until} закончился, оплатить по ней нельзя. Так мы страхуем вас от оплаты по устаревшей цене — попросите менеджера выставить новую.`,
        detailsShort);
    }

    // ── Ссылка активна ──
    const hasQuotas = hasQuota(plan.chat_messages_per_30d) || hasQuota(plan.voice_seconds_per_30d);

    // Подпись под кнопкой оплаты. Собирается, а не выбирается из готовых
    // вариантов: способов стало три, и матрица «способы × автопродление»
    // разрослась бы до восьми строк, которые расходятся при первой же правке.
    const payHint = (() => {
      const ways = ['картой через Робокассу'];
      if (hasInvoice) ways.push('по счёту для юрлица');
      if (hasForeign) ways.push('иностранной картой');
      const list = ways.length > 1
        ? `${ways.slice(0, -1).join(', ')} или ${ways[ways.length - 1]}`
        : ways[0];
      // Автопродление обещаем ТОЛЬКО когда ссылка выставлена с ним (решение
      // менеджера, сведённое с тарифом, — сервер отдаёт `auto_renew`) И ТОЛЬКО
      // за российскую карту: у счёта рекуррента нет, а инокарту на такой
      // ссылке и не предложат. Общее «продлевается автоматически» было бы
      // прямой неправдой тому, кто выбрал другой способ.
      const tail = !d.auto_renew
        ? 'Разовый платёж, автоматических списаний не будет.'
        : (hasInvoice
          ? 'При оплате российской картой тариф дальше продлевается автоматически — отключить можно в настройках. По счёту — разовый платёж, продление вручную.'
          : 'Дальше тариф продлевается автоматически — отключить можно в настройках.');
      // Постороннему плательщику «отключить можно в настройках» ничего не
      // говорит: настроек у него нет, а списываться будет ЕГО карта. Условия
      // сделки мы не меняем (так менеджер её и продал) — но обязаны назвать их
      // вслух тому, чья карта привязывается, иначе следующее списание он
      // прочитает как мошенничество.
      const guestTail = guestPays && d.auto_renew
        ? ' Карта, которой оплачен счёт, будет списываться и в следующие периоды;'
          + ' отключить автопродление сможет владелец аккаунта в своих настройках.'
        : '';
      return `Оплата ${list}. ${tail}${guestTail}`;
    })();

    // Что оплата сделает с текущим сроком — решает сервер (`period_effect`), см.
    // periodNotice. Выводить это здесь из «Сейчас у вас» больше нельзя: так
    // страница обещала сгоревшие дни и продлению заранее, и акции в очереди.
    const notice = periodNotice(d);

    // Состав счёта сервер отдаёт всем, кто вправе его оплатить: оплатить, не
    // видя суммы, невозможно. В режиме «новое пространство» состав по-прежнему
    // только владельцу — там и платить может только он, — поэтому рисуем блок
    // ровно тем, кому сервер эти поля прислал. Иначе посторонний увидел бы
    // «Тариф —, Период —» и пустую сумму, то есть сломанную страницу.
    const details = !canPay ? null : (
      <div className="checkout-details">
        {/* Название заведения видит и посторонний плательщик: бухгалтер сети
            иначе не отличит этот счёт от соседнего и оплатит не тот. */}
        {d.workspace && <CheckoutRow label="Пространство" value={d.workspace.name} />}
        {mine && isNewWs && (
          <CheckoutRow label="Пространство" value="Новое — создадим после оплаты" />
        )}
        {/* «Сейчас у вас» — про подписку, которую оплата ЗАМЕНИТ. У нового
            пространства заменять нечего: строка сбивала бы с толку тарифом
            личного ассистента, к покупке заведения отношения не имеющим. */}
        {mine && !isNewWs && (
          <CheckoutRow label="Сейчас у вас"
            value={cur
              ? cur.name + (cur.expires_at ? `, до ${fmt.date(cur.expires_at)}` : '')
              : 'Тариф не подключён'} />
        )}
        <CheckoutRow label={mine && cur ? 'Новый тариф' : 'Тариф'} value={plan.name || '—'} />
        <CheckoutRow label="Период" value={days(plan.period_days)} />
        {/* Автопродление — решение МЕНЕДЖЕРА на выпуске ссылки, сведённое с
            возможностями тарифа; сервер отдаёт его уже выведенным. Спрашивать
            здесь `plan.renewable` нельзя: разовая продажа обычного тарифа
            показала бы «Включено» и пообещала списания, которых не будет. */}
        <CheckoutRow label="Автопродление" value={d.auto_renew ? 'Включено' : 'Нет, разовый платёж'} />
        {hasQuotas && <div className="paylink__group-title">Что входит</div>}
        {hasQuota(plan.chat_messages_per_30d) && (
          <CheckoutRow label="Запросы к ассистенту" value={requests(plan.chat_messages_per_30d)} />
        )}
        {hasQuota(plan.voice_seconds_per_30d) && (
          <CheckoutRow label="Голосовой ввод" value={voiceMinutes(plan.voice_seconds_per_30d)} />
        )}
        <div className="paylink__total"><CheckoutRow label="К оплате" value={money(d.amount_rub)} /></div>
      </div>
    );

    return (
      <CheckoutLayout>
        <div className="paylink">
          <header className="checkout-head checkout-head--center">
            <div className="checkout-icon checkout-icon--accent" aria-hidden="true"><Icon name="card" size={28} /></div>
            <div className="t-eyebrow text-dim">Счёт от менеджера</div>
            <h1 className="t-h2">{mine ? 'Оплата подписки' : 'Счёт на оплату'}</h1>
          </header>

          {details}

          {d.expires_at && (
            <p className="paylink__note">Ссылка действует до {fmt.date(d.expires_at)}.</p>
          )}

          {/* Платит посторонний: объясняем, чей это счёт, ДО кнопки. Бухгалтер
              должен убедиться, что платит за своё заведение, а не за соседнее,
              и понимать, что доступ получит клиент, а не он сам. Оплату при
              этом не загораживаем — ради неё он сюда и пришёл. */}
          {guestPays && (
            <div className="callout callout--info" role="status">
              <Icon name="lock" size={16} />
              {/* Текст держим коротким намеренно: на телефоне каждая лишняя
                  строка отодвигает кнопку оплаты вниз, а счёт открывают именно
                  с телефона. Фактов ровно три, и все нужны: чей это счёт, что
                  вход не требуется, и что подписку получит клиент, а не
                  плательщик. */}
              <span>
                Счёт для аккаунта{' '}
                <strong className="paylink__masked">{d.client_email_masked || '—'}</strong>
                {user && user.email
                  ? <> — вы вошли как <span className="paylink__masked">{user.email}</span></>
                  : null}
                . Входить, чтобы оплатить, не нужно: подписка подключится этому аккаунту.
                {' '}
                {/* Вход остаётся ссылкой, а не исчезает вовсе: что оплата
                    сделает с текущим сроком (`period_effect` — продлит, встанет
                    в очередь или сожжёт остаток), сервер говорит только
                    КЛИЕНТУ, и без входа он этого не увидит. Заплатить и
                    обнаружить сгоревшие дни после — ровно то, от чего это
                    предупреждение и заводилось. */}
                <a className="link" href="/signin"
                  onClick={(e) => { e.preventDefault(); saveAndSignIn(); }}>
                  {d.authenticated ? 'Войти под ним' : 'Это ваш счёт? Войдите'}
                </a>
                {' '}— увидите, как оплата повлияет на текущий тариф.
              </span>
            </div>
          )}

          {/* «Новое пространство»: платить может только клиент — название
              заведения знает он один. Та же развилка, что у приглашений
              (InviteAccept, S12): сохранить токен → /signin, без logout. */}
          {needsClientLogin && (
            <>
              <div className="callout callout--warn" role="status">
                <Icon name="alert-triangle" size={16} />
                <span>
                  Ссылка выставлена для аккаунта{' '}
                  <strong className="paylink__masked">{d.client_email_masked || '—'}</strong>
                  {user && user.email ? <>, а вы вошли как <span className="paylink__masked">{user.email}</span></> : null}.
                  По ней создаётся новое заведение, поэтому оплатить её может только он.
                </span>
              </div>
              <div className="checkout-actions">
                <Button variant="primary" size="lg" className="full" onClick={saveAndSignIn}>
                  {d.authenticated ? 'Сменить аккаунт' : 'Войти и оплатить'}
                </Button>
                <a className="link checkout-link" href={homePath}
                  onClick={(e) => { e.preventDefault(); navigate(homePath); }}>{homeLabel}</a>
              </div>
            </>
          )}

          {/* Оплата — свой счёт либо чужой, который мы вправе оплатить */}
          {canPay && (
            <>
              {/* Что станет с текущим сроком — ДО оплаты: продлится от конца,
                  акция встанет в очередь, или (другой тариф) срок начнётся
                  заново и остаток сгорит. Последнее клиент обязан узнать до
                  оплаты, а не обнаружить сгоревшие дни после. */}
              {notice && (
                <div className={'callout callout--' + notice.tone} role="status"
                  data-testid="paylink-period-notice">
                  <Icon name={notice.icon} size={16} />
                  <span>{notice.text}</span>
                </div>
              )}
              {/* Название заведения — ДО оплаты: пространство создаётся в вебхуке
                  провайдера, где спросить уже некого. Поле обязательное, поэтому
                  стоит над кнопкой, а не прячется в модалке способа оплаты. */}
              {isNewWs && (
                <Field label="Название заведения" required htmlFor="paylink-ws-name"
                  hint="Так пространство будет называться в приложении. Переименовать можно в любой момент в настройках."
                  error={wsNameTouched && !wsNameOk
                    ? 'Введите название — минимум два символа.' : undefined}>
                  <TextInput id="paylink-ws-name" value={wsName} maxLength={120}
                    autoComplete="organization"
                    placeholder="Например: Кафе «Пельмень» на Кирочной"
                    invalid={wsNameTouched && !wsNameOk}
                    // Ошибку снимаем сразу при правке: висящий красный блок
                    // «укажите название» над уже заполненным полем читается как
                    // «оплата всё равно не пройдёт».
                    onChange={(e) => {
                      setWsName(e.target.value); setWsNameTouched(true); setPayErr(null);
                    }} />
                </Field>
              )}
              {payErr && (
                <div className="callout callout--bad" role="alert">
                  <Icon name="alert-circle" size={16} /><span>{payErr}</span>
                </div>
              )}
              <div className="checkout-actions">
                <Button variant="primary" size="lg" className="full" iconLeft="card"
                  loading={paying} disabled={paying} onClick={payClick}>
                  {paying ? 'Переходим к оплате…' : `Оплатить ${money(d.amount_rub)}`}
                </Button>
                <p className="paylink__note">
                  {isNewWs && 'После оплаты мы создадим пространство и сразу переключим вас в него. '}
                  {payHint}
                </p>
              </div>
            </>
          )}
        </div>

        {/* Развилка способа оплаты — тот же компонент, что при покупке тарифа
            из ЛК. Своя копия неизбежно разошлась бы с оригиналом в текстах,
            которыми мы объясняем клиенту, за что списываются деньги. */}
        {/* Шаг «оставьте телефон» — вне условных веток способов оплаты: он нужен
            одинаково и там, где развилка есть, и там, где сразу карта. Только
            своему клиенту: у постороннего плательщика профиля нет. */}
        {mine && phoneGate.modal}
        {canPay && (hasInvoice || hasForeign) && (
          <PaymentMethodModal
            open={methodOpen}
            plan={{ name: plan.name, price_rub: d.amount_rub }}
            busy={paying}
            showInvoice={hasInvoice}
            showForeign={hasForeign}
            // Акция по ссылке встаёт в очередь (`period_effect.queued`) — окно
            // скажет дату старта, а не «активируется сразу»; ссылка на тот же
            // идущий тариф (`period_effect.extends`) — «продлит текущий срок до …».
            // Гостю-плательщику сервер дат не шлёт (S10) — окно как прежде.
            startsAt={(notice && notice.startsAt) || null}
            extendsTo={(notice && notice.extendsTo) || null}
            onClose={() => setMethodOpen(false)}
            // Клик пробрасываем стрелкой, а не ссылкой на payByCard: onClick
            // передал бы первым аргументом событие, и оно уехало бы в `method`.
            onPickCard={() => payByCard('card')}
            onPickForeign={() => payByCard('foreign_card')}
            onPickInvoice={() => { setMethodOpen(false); setInvoiceOpen(true); }} />
        )}
        {/* Согласие на автосписания — последний шаг перед Робокассой у ссылки с
            автопродлением. Тот же компонент, что при покупке тарифа из ЛК.
            «Далее автоматически» — цена ТАРИФА (её спишет продление), а не сумма
            счёта: менеджер мог назвать за первый период другую. */}
        {canPay && (
          <RecurringConsentModal
            open={consentOpen}
            planName={plan.name}
            priceRub={plan.price_rub}
            periodDays={plan.period_days}
            payNowRub={d.amount_rub}
            guestPays={guestPays}
            busy={paying}
            onClose={() => setConsentOpen(false)}
            onConfirm={() => payByCard('card', true)} />
        )}
        {canPay && hasInvoice && (
          <InvoicePayModal
            open={invoiceOpen}
            onClose={() => setInvoiceOpen(false)}
            // Префилл реквизитов — только своему клиенту: `/payer-requisites/`
            // сессионный, постороннему он отдаст 403, а главное — ИНН и
            // юрназвание клиента постороннему показывать незачем. Гость
            // заполняет форму с нуля, своими реквизитами.
            wsId={mine ? (d.workspace && d.workspace.id) : undefined}
            canPrefill={mine}
            planLabel={plan.name}
            amountRub={d.amount_rub}
            // Счёт выставляет эндпоинт ссылки: тариф, пространство и сумма
            // берутся из неё, а не из сессии клиента.
            submitInvoice={async (requisites) => {
              const r = await startPaymentLinkInvoice(
                token, requisites, isNewWs ? wsNameClean : undefined);
              if (!r.ok) throw (r.data || { detail: 'Не удалось выставить счёт.' });
              return r.data;
            }}
            onCreated={(invoiceId) => {
              setInvoiceOpen(false);
              // `?pl=` — пропуск на страницу счёта для того, у кого нет сессии:
              // экран по нему читает состояние и PDF через токен ссылки. Без
              // этого бухгалтер после «Выставить счёт» улетал бы на /signin,
              // то есть терял бы платёжку, которую и пришёл забрать.
              navigate('/checkout/invoice/' + encodeURIComponent(invoiceId)
                + (mine ? '' : '?pl=' + encodeURIComponent(token)));
            }} />
        )}
      </CheckoutLayout>
    );
  }

export { PayByLink, payerView, periodNotice };
